import { cn } from '@/lib'
import { tz } from '@date-fns/tz'
import { format } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
	bookingToLocalBlock,
	calculateSlots,
	CALENDAR_HEIGHT_PX,
	CALENDAR_HOUR_LABELS,
	CALENDAR_TOTAL_HOURS,
	durationToPx,
	formatHourLabel,
	getCurrentWeekDates,
	getEffectiveWindows,
	HOUR_HEIGHT_PX,
	minutesToPx,
	SLOT_DURATION_MINUTES,
} from './calendar-helpers'
import type { FormState } from './types'

interface ConfirmedBooking {
	scheduledAt: string
	durationMinutes: number
}

export function WeekCalendarPreview(
	{ form, confirmedBookings = [], bookingsLoading = false, bookingsError = false }: {
		form: FormState
		confirmedBookings?: ConfirmedBooking[]
		bookingsLoading?: boolean
		bookingsError?: boolean
	},
) {
	const { t } = useTranslation('common')
	// weekDates is browser-timezone-based; timezone-aware ISO week computation requires a larger refactor
	const weekDates = useMemo(() => getCurrentWeekDates(), [])
	const todayStr = useMemo(
		() => format(new Date(), 'yyyy-MM-dd', { in: tz(form.timezone) }),
		[form.timezone],
	)

	const advanceNoticeCutoff = useMemo(() => {
		if (!form.minAdvanceNoticeHours) {
			return null
		}
		const cutoff = new Date(Date.now() + form.minAdvanceNoticeHours * 60 * 60 * 1000)
		const dateStr = format(cutoff, 'yyyy-MM-dd', { in: tz(form.timezone) })
		const minutesOfDay = parseInt(format(cutoff, 'H', { in: tz(form.timezone) })) * 60
			+ parseInt(format(cutoff, 'm', { in: tz(form.timezone) }))
		return { dateStr, minutesOfDay }
	}, [form.minAdvanceNoticeHours, form.timezone])

	const dayNames = [
		t('account.availability.days.mon'),
		t('account.availability.days.tue'),
		t('account.availability.days.wed'),
		t('account.availability.days.thu'),
		t('account.availability.days.fri'),
		t('account.availability.days.sat'),
		t('account.availability.days.sun'),
	]

	return (
		<div className="bg-white rounded-xl border border-grey-100 p-4 flex flex-col gap-3">
			<div className="flex items-center justify-between gap-2">
				<h2 className="text-xs font-medium text-grey-600 uppercase tracking-wide">
					{t('account.availability.preview.title')}
				</h2>
				{bookingsError
					? (
						<span className="text-xs text-red-500 text-right">
							{t('account.availability.preview.bookings_error')}
						</span>
					)
					: bookingsLoading
					? (
						<span className="text-xs text-grey-400 text-right">
							{t('account.availability.preview.bookings_loading')}
						</span>
					)
					: (
						<span className="text-xs text-grey-400 text-right">
							{t('account.availability.preview.subtitle')}
						</span>
					)}
			</div>

			<div className="flex gap-1">
				{/* Time axis */}
				<div className="shrink-0" style={{ width: 28 }}>
					<div style={{ height: 28 }} />
					<div className="relative" style={{ height: CALENDAR_HEIGHT_PX }}>
						{CALENDAR_HOUR_LABELS.map((hour) => (
							<span
								key={hour}
								className="absolute right-1 text-xs leading-none text-grey-400 -translate-y-1.5"
								style={{ top: minutesToPx(hour * 60) }}
							>
								{formatHourLabel(hour)}
							</span>
						))}
					</div>
				</div>

				{/* Day columns */}
				<div className="flex-1 flex gap-0.5">
					{weekDates.map((date, colIdx) => {
						const dateStr = format(date, 'yyyy-MM-dd', { in: tz(form.timezone) })
						const isToday = dateStr === todayStr
						const { windows, fullyBlocked: scheduleFullyBlocked, blockedWindows } = getEffectiveWindows(
							date,
							form,
						)
						const advanceFullyBlocked = advanceNoticeCutoff !== null
							&& dateStr < advanceNoticeCutoff.dateStr
						const advanceCutoffMinutes =
							advanceNoticeCutoff !== null && dateStr === advanceNoticeCutoff.dateStr
								? advanceNoticeCutoff.minutesOfDay
								: 0
						const fullyBlocked = scheduleFullyBlocked || advanceFullyBlocked
						const effectiveBlockedWindows = advanceCutoffMinutes > 0
							? [...blockedWindows, { start: 0, end: advanceCutoffMinutes }]
							: blockedWindows
						const slots = calculateSlots(
							windows,
							effectiveBlockedWindows,
							form.bufferMinutes,
							form.maxBookingsPerDay,
							advanceCutoffMinutes,
						)

						return (
							<div key={colIdx} className="flex-1 flex flex-col min-w-0">
								<div className="text-center mb-1" style={{ height: 28 }}>
									<div
										className={cn(
											'text-xs font-medium leading-tight',
											isToday ? 'text-brand-600' : 'text-grey-500',
										)}
									>
										{dayNames[colIdx]}
									</div>
									<div
										className={cn(
											'text-xs leading-tight',
											isToday ? 'text-brand-600 font-semibold' : 'text-grey-400',
										)}
									>
										{date.getDate()}
									</div>
								</div>

								<div
									className="relative rounded border border-grey-100 overflow-hidden bg-grey-50/30"
									style={{ height: CALENDAR_HEIGHT_PX }}
								>
									{Array.from({ length: CALENDAR_TOTAL_HOURS }, (_, i) => (
										<div
											key={i}
											className="absolute inset-x-0 border-t border-grey-100/70"
											style={{ top: i * HOUR_HEIGHT_PX }}
										/>
									))}

									{fullyBlocked
										? (
											<div className="absolute inset-0 bg-red-50/80 flex items-center justify-center">
												<span
													className="text-[9px] text-red-400 font-medium"
													style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
												>
													{t('account.availability.preview.unavailable_label')}
												</span>
											</div>
										)
										: (
											<>
												{windows.map((window, wi) => {
													const top = Math.max(0, minutesToPx(window.start))
													const bottom = Math.min(CALENDAR_HEIGHT_PX, minutesToPx(window.end))
													if (bottom <= top) { return null }
													return (
														<div
															key={wi}
															className="absolute inset-x-0 bg-brand-50"
															style={{ top, height: bottom - top }}
														/>
													)
												})}

												{effectiveBlockedWindows.map((bw, bi) => {
													const top = Math.max(0, minutesToPx(bw.start))
													const bottom = Math.min(CALENDAR_HEIGHT_PX, minutesToPx(bw.end))
													if (bottom <= top) { return null }
													return (
														<div
															key={bi}
															className="absolute inset-x-0 bg-red-50/80"
															style={{ top, height: bottom - top }}
														/>
													)
												})}

												{slots.map((slot, si) => {
													const top = minutesToPx(slot.start)
													const height = durationToPx(SLOT_DURATION_MINUTES)
													if (top < 0 || top >= CALENDAR_HEIGHT_PX) { return null }
													return (
														<div
															key={si}
															className={cn(
																'absolute inset-x-0.5 border border-dashed rounded-sm',
																slot.capped
																	? 'border-grey-300 bg-grey-100/50'
																	: 'border-brand-300 bg-white/90',
															)}
															style={{
																top,
																height: Math.min(height, CALENDAR_HEIGHT_PX - top),
															}}
														/>
													)
												})}

												{confirmedBookings
													.map((booking) =>
														bookingToLocalBlock(
															booking.scheduledAt,
															booking.durationMinutes,
															form.timezone,
														)
													)
													.filter((block) => block.dateStr === dateStr)
													.map((block, bi) => {
														const top = Math.max(0, minutesToPx(block.startMinutes))
														const bottom = Math.min(
															CALENDAR_HEIGHT_PX,
															minutesToPx(block.endMinutes),
														)
														if (bottom <= top) { return null }
														return (
															<div
																key={bi}
																className="absolute inset-x-0.5 rounded-sm bg-amber-400/80"
																style={{ top, height: bottom - top }}
															/>
														)
													})}
											</>
										)}
								</div>
							</div>
						)
					})}
				</div>
			</div>

			{/* Legend */}
			<div className="flex items-center gap-4">
				<div className="flex items-center gap-1.5">
					<div className="w-3 h-3 rounded-sm border border-dashed border-brand-300 bg-white" />
					<span className="text-xs text-grey-500">
						{t('account.availability.preview.legend_available_slot')}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<div className="w-3 h-3 rounded-sm border border-dashed border-grey-300 bg-grey-100" />
					<span className="text-xs text-grey-500">
						{t('account.availability.preview.legend_past_limit')}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<div className="w-3 h-3 rounded-sm bg-red-50 border border-red-200" />
					<span className="text-xs text-grey-500">
						{t('account.availability.preview.legend_unavailable')}
					</span>
				</div>
				{confirmedBookings.length > 0 && (
					<div className="flex items-center gap-1.5">
						<div className="w-3 h-3 rounded-sm bg-amber-400/80" />
						<span className="text-xs text-grey-500">
							{t('account.availability.preview.legend_confirmed_booking')}
						</span>
					</div>
				)}
			</div>
		</div>
	)
}
